import { createApp } from "vue";
import { trackPage } from "./services/analytics";
import { trackPageView, routeTemplate } from "./services/stats";
import { reloadOnChunkError } from "./utils/chunkReload";
import { initInstallPrompt } from "./composables/useInstallPrompt";
import { createWebHistory, createRouter } from "vue-router";

// styles

// FontAwesome and Flowbite were imported globally but are unused: no template
// renders an `fa-*` icon (the footer socials are inline SVG, the FAQ/config
// "far" hits are the Italian word, not a class) and no Flowbite data-attribute
// or API is used anywhere. Importing them shipped ~70 KB of icon CSS + the FA
// webfonts and Flowbite's JS on every page for nothing, so both are dropped.
// import "./tailwind.css";
// mouting point for the whole app

// L'evento di installazione arriva una volta sola e presto: va ascoltato subito.
initInstallPrompt();

import App from "@/App.vue";

// views

// Home stays eager for LCP on the landing page; all other route components
// are code-split (see docs/seo/indexing-plan.md, Fase 4).
import Home from "@/views/Home.vue";
const fb = require('./firebase/init.js');
import firebase from 'firebase/compat/app';

import './index.css'
import "@/assets/styles/popup.css";
import { globalStore } from './globalStore';
import { signedOutRedirect } from './utils/publicPaths.js';
import { initI18n, i18nPlugin } from './i18n';

const Profile = () => import("./views/user/Profile.vue");
const LandingIframe = () => import("@/views/LandingIframe.vue");
const Configure = () => import("@/views/Configure.vue");
const ConfirmAddressStep = () => import("@/components/ConfiguratorSteps/ConfirmAddressStep.vue");
const TypologyStep = () => import("@/components/ConfiguratorSteps/TypologyStep.vue");
const HouseStatusStep = () => import("@/components/ConfiguratorSteps/HouseStatusStep.vue");
const ImageLoaderStep = () => import("@/components/ConfiguratorSteps/ImageLoaderStep.vue");
const PlanimetryDetailsStep = () => import("@/components/ConfiguratorSteps/PlanimetryDetailsStep.vue");
const PlanimetryDetailsStepPart2 = () => import("@/components/ConfiguratorSteps/PlanimetryDetailsStepPart2.vue");
const PlanimetryDetailsStepPart3 = () => import("@/components/ConfiguratorSteps/PlanimetryDetailsStepPart3.vue");
const AdditionalInfoStep = () => import("@/components/ConfiguratorSteps/AdditionalInfoStep.vue");
const ValuationStep = () => import("@/components/ConfiguratorSteps/ValuationStep.vue");
const SingleProperty = () => import("@/views/SingleProperty.vue");
const PrenotaVideocall = () => import("@/views/PrenotaVideocall.vue");
const Vendi = () => import("@/views/Vendi.vue");
const CompraRedesign = () => import("@/views/CompraRedesign.vue");
// Kept in place (dead) after /compra-agency was re-routed to CompraRedesign.
// eslint-disable-next-line no-unused-vars
const OptimisedAgencyPropertyListing = () => import("./views/OptimisedAgencyPropertyListing.vue");
const LikedAds = () => import("./views/user/LikedAds.vue");
const RenovationQuote = () => import("./views/user/RenovationQuote.vue");
const AddRenovationQuote = () => import("./views/user/AddRenovationQuote.vue");
const RenovationQuoteRecap = () => import("./views/user/RenovationQuoteRecap.vue");
const VideocallRequests = () => import("./views/user/VideocallRequests.vue");
const VideocallRequestDetail = () => import("./views/user/VideocallRequestDetail.vue");
const PingList = () => import("./views/user/PingList.vue");
const AdConfiguration = () => import("./views/user/AdConfiguration.vue");
const LoginIframe = () => import("./views/LoginIframe.vue");
const Faq = () => import("@/views/Faq.vue");
const Agenzie = () => import("@/views/Agenzie.vue");
const PrivacyPolicy = () => import("@/views/PrivacyPolicy.vue");
const CookiePolicy = () => import("@/views/CookiePolicy.vue");
// Documenti legali e segnalazioni (#815).
const TerminiCondizioni = () => import("@/views/TerminiCondizioni.vue");
const CondizioniAgenzie = () => import("@/views/CondizioniAgenzie.vue");
const AccordoTrattamentoDati = () => import("@/views/AccordoTrattamentoDati.vue");
const NoteLegali = () => import("@/views/NoteLegali.vue");
const Segnala = () => import("@/views/Segnala.vue");


const requireAuth = (to, from, next) => {
  const currentUser = firebase.auth().currentUser;

  if (currentUser && currentUser.emailVerified) {
    next();
  } else {
    next('/annunci');
  }
};


// routes

const routes = [
  
  
  {
    path: "/landing-iframe",
    component: LandingIframe,
  },
  {
    path: "/profile",
    component: Profile,
  },
  {
    path: "/annunci",
    component: CompraRedesign,
  },
  {
    // Legacy listing URLs: /compra and /compra/<id> became /annunci and
    // /annunci/<id> (the SSR renderer 301s them for crawlers).
    path: "/compra",
    redirect: "/annunci",
  },
  {
    path: "/compra/:docId",
    redirect: (to) => ({ path: "/annunci/" + encodeURIComponent(to.params.docId), query: to.query, hash: to.hash }),
  },
  {
    path: "/",
    component: Home,
  },
  {
    path: "/faq",
    component: Faq,
  },
  {
    path: "/agenzie",
    component: Agenzie,
  },
  {
    // Agency page, immobiliare.it-style: /agenzie/<code>/<nome-agenzia>. The
    // code is resolved to the agency by CompraRedesign; the slug is cosmetic.
    // Declared after /agenzie, which stays the B2B page.
    path: '/agenzie/:agencyCode(\\d+)/:agencySlug?',
    component: CompraRedesign,
    props: (route) => ({ agencyCode: route.params.agencyCode }),
  },
  {
    // Legacy name-only URL: CompraRedesign replaces it with the code one.
    path: '/agenzie/:agencySlug',
    component: CompraRedesign,
    props: (route) => ({ agencySlug: route.params.agencySlug }),
  },
  {
    // Legacy id URL, still served (the SSR renderer 301s it).
    path: '/compra-agency/:agencyID',
    component: CompraRedesign,
    props: (route) => ({ agencyId: route.params.agencyID }),
  },
  {
    path: '/user/profile',
    component: Profile,
    props: true,
    alias: '/user/profile',
    beforeEnter: requireAuth
  },
  {
    path: '/user/favorite',
    component: LikedAds,
    props: true,
    alias: '/user/favorite',
    beforeEnter: requireAuth
  },
  {
    path: '/user/preventivi-ristrutturazione',
    component: RenovationQuote,
    props: true,
    alias: '/user/preventivi-ristrutturazione',
    beforeEnter: requireAuth
  },
  {
    path: '/user/preventivi-ristrutturazione/add',
    component: AddRenovationQuote,
    props: true,
    alias: '/user/preventivi-ristrutturazione/add',
    // beforeEnter: requireAuth
  },
  {
    path: '/user/preventivi-ristrutturazione/:docId',
    component: RenovationQuoteRecap,
    props: true,
    // beforeEnter: requireAuth
  },
  {
    path: '/user/videochiamate',
    component: VideocallRequests,
    props: true,
    alias: '/user/videochiamate',
    beforeEnter: requireAuth
  },
  {
    path: '/user/videochiamate/:docId',
    component: VideocallRequestDetail,
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/user/configuration',
    component: AdConfiguration,
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/user/ping',
    component: PingList,
    props: true,
    alias: '/user/ping',
    beforeEnter: requireAuth
  },
  {
    path: '/annunci/:docId',
    name: "PropertyListing",
    component: SingleProperty,
    props: true,
  },
  {
    path: "/prenota-videocall",
    component: PrenotaVideocall,
  },
  {
    path: "/vendi",
    component: Vendi,
  },
  {
    path: "/privacy-policy",
    component: PrivacyPolicy,
  },
  {
    path: "/cookie-policy",
    component: CookiePolicy,
  },
  {
    path: "/termini-e-condizioni",
    component: TerminiCondizioni,
  },
  {
    path: "/condizioni-agenzie",
    component: CondizioniAgenzie,
  },
  {
    path: "/accordo-trattamento-dati",
    component: AccordoTrattamentoDati,
  },
  {
    path: "/note-legali",
    component: NoteLegali,
  },
  {
    path: "/segnala",
    component: Segnala,
  },
  {
    path: '/iLogin',
    name: "iLogin",
    component: LoginIframe,
    props: true,
    alias: '/iLogin'
    
  },
  {
    path: "/configure",
    component: Configure,
    children: [
      {
        name: 'step-0',
        path: "/configure/conferma-indirizzo",
        component: ConfirmAddressStep,
      },
      {
        name: 'step-1',
        path: "/configure/tipologia",
        component: TypologyStep,
      },
      {
        name: 'step-2',
        path: "/configure/stato",
        component: HouseStatusStep,
      },
      {
        name: 'step-3',
        path: "/configure/images",
        component: ImageLoaderStep,
      },
      {
        name: 'step-4',
        path: "/configure/caratteristiche1",
        component: PlanimetryDetailsStep,
      },
      {
        name: 'step-5',
        path: "/configure/caratteristiche2",
        component: PlanimetryDetailsStepPart2,
      },
      {
        name: 'step-6',
        path: "/configure/caratteristiche3",
        component: PlanimetryDetailsStepPart3,
      },
      {
        name: 'step-7',
        path: "/configure/informazioni-aggiuntive",
        component: AdditionalInfoStep,
      },
      {
        name: 'valuation',
        path: "/configure/valuation",
        component: ValuationStep,
      }
    ]
  },
  // { path: "/:pathMatch(.*)*", redirect: "/configure" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Start each new navigation at the top of the page; keep the previous
  // position when using the browser back/forward buttons, and honor #anchors.
  // A query-only change on the same page (e.g. ?anteprima= in /vendi) keeps
  // the scroll position.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.path === from.path && to.hash === from.hash) return false;
    if (to.hash) return { el: to.hash };
    return { top: 0 };
  },
});

// GA4 SPA page_views (#501) — see src/services/analytics.js for the
// Enhanced-Measurement double-counting note.
// Statistiche di prima parte (#652): il collector riceve il template della
// route (es. /annunci/:docId), mai il path concreto né la query string.
router.afterEach((to) => {
  trackPage(to.fullPath);
  trackPageView(routeTemplate(to));
});


// #736: chunk della versione precedente spariti dopo un deploy → ricarica la
// pagina richiesta invece di lasciare il clic senza effetto (vedi il modulo).
router.onError((error, to) => {
  reloadOnChunkError(error, to);
});

export function waitForAuthReady() {
  return new Promise((resolve) => {
    const unsubscribe = fb.auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

router.beforeEach(async (to, from, next) => {
  const user = await waitForAuthReady();
  const redirect = signedOutRedirect(user, to.path);
  if (redirect) return next(redirect);

  next();
});

// Lingua del sito (src/i18n/): con ?lang= o una scelta ricordata il
// dizionario arriva prima di montare l'app, così la pagina non compare prima
// in italiano. Senza scelta non scarica niente.
const i18nReady = initI18n().catch(() => {});

let app;
fb.auth.onAuthStateChanged((user) => {
  if (!app) {
    app = createApp(App);
    app.provide('globalStore', globalStore);
    app.use(router);
    app.use(i18nPlugin);
    i18nReady.then(() => app.mount('#app'));
    return;
  }
  // #888: a session that ends on an open page (getUserDetails signs a
  // platform account out) leaves it, as the guard would have.
  const redirect = signedOutRedirect(user, router.currentRoute.value.path);
  if (redirect) router.replace(redirect);
});

export default router;
